import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { lcoPaymentAPI } from '../services/api';

export default function LCOPaymentSetup() {
  const { tenant } = useAuth();
  const primary = tenant?.branding?.primary_color || '#1B6B3A';
  const [setup, setSetup] = useState({business_name:'', upi_id:'', contact_phone:''});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [tab, setTab] = useState('setup'); // setup | android | howto
  const [androidConfig, setAndroidConfig] = useState(null);

  useEffect(() => {
    lcoPaymentAPI.getAndroidConfig().then(r=>{ if(r.data?.success) setAndroidConfig(r.data); }).catch(()=>{});
    lcoPaymentAPI.getSetup().then(r => {
      if(r.data?.setup) setSetup(r.data.setup);
      setLoading(false);
    }).catch(()=>setLoading(false));
  }, []);

  const save = async () => {
    if(!setup.upi_id) return setMsg({type:'error', text:'UPI ID is required'});
    if(!/^[\w.\-]+@[\w]+$/.test(setup.upi_id.trim())) return setMsg({type:'error', text:'Invalid UPI ID. Format: name@bank (e.g. yourname@okicici)'});
    setSaving(true);
    try {
      await lcoPaymentAPI.saveSetup(setup);
      setMsg({type:'success', text:'Payment setup saved! Customers can now pay directly to your UPI.'});
    } catch(e) { setMsg({type:'error', text:e.response?.data?.message||'Failed to save'}); }
    setSaving(false);
  };

  if(loading) return <div style={{padding:'40px',textAlign:'center',color:'#94a3b8'}}>Loading...</div>;

  return (
    <div style={{padding:'20px',maxWidth:'800px',margin:'0 auto'}}>
      {/* Header */}
      <div style={{background:'white',borderRadius:'16px',padding:'20px 24px',marginBottom:'20px',
        boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
        <h2 style={{margin:0,fontSize:'20px',fontWeight:'900',color:'#0f172a'}}>💳 Payment Setup</h2>
        <p style={{margin:'6px 0 0',fontSize:'13px',color:'#64748b'}}>
          Set up direct UPI payments — customers pay straight to your bank account with zero fees
        </p>
      </div>

      {/* Status Banner */}
      {setup.is_active && (
        <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'12px',
          padding:'12px 16px',marginBottom:'16px',display:'flex',alignItems:'center',gap:'10px'}}>
          <span style={{fontSize:'20px'}}>✅</span>
          <div>
            <div style={{fontWeight:'700',color:'#16a34a',fontSize:'13px'}}>Payment Gateway Active</div>
            <div style={{fontSize:'12px',color:'#15803d'}}>Customers can pay via UPI to {setup.upi_id}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:'flex',gap:'8px',marginBottom:'16px'}}>
        {[['setup','⚙️ Setup'],['android','📱 Android App'],['howto','📖 How It Works']].map(([v,l])=>(
          <button key={v} onClick={()=>setTab(v)}
            style={{padding:'10px 18px',border:'2px solid',borderRadius:'10px',cursor:'pointer',
              fontSize:'13px',fontWeight:'700',borderColor:tab===v?primary:'#e2e8f0',
              background:tab===v?primary:'white',color:tab===v?'white':'#64748b'}}>
            {l}
          </button>
        ))}
      </div>

      {/* Setup Tab */}
      {tab==='setup' && (
        <div style={{background:'white',borderRadius:'16px',padding:'24px',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
          <div style={{marginBottom:'20px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',
              letterSpacing:'0.5px',marginBottom:'8px'}}>Business Name</div>
            <input value={setup.business_name||''} onChange={e=>setSetup({...setup,business_name:e.target.value})}
              placeholder="e.g. Krishna Communications Pay"
              style={{width:'100%',padding:'12px 16px',border:'1px solid #e2e8f0',borderRadius:'10px',
                fontSize:'14px',boxSizing:'border-box',outline:'none'}}/>
            <div style={{fontSize:'11px',color:'#94a3b8',marginTop:'4px'}}>This name appears on customer payment screen</div>
          </div>

          <div style={{marginBottom:'20px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',
              letterSpacing:'0.5px',marginBottom:'8px'}}>Your UPI ID *</div>
            <input value={setup.upi_id||''} onChange={e=>setSetup({...setup,upi_id:e.target.value})}
              placeholder="e.g. yourname@okicici or 9876543210@ybl"
              style={{width:'100%',padding:'12px 16px',border:'1px solid #e2e8f0',borderRadius:'10px',
                fontSize:'14px',boxSizing:'border-box',outline:'none'}}/>
            <div style={{fontSize:'11px',color:'#94a3b8',marginTop:'4px'}}>
              Find your UPI ID in GPay → Profile, or PhonePe → Profile
            </div>
          </div>

          <div style={{marginBottom:'24px'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',
              letterSpacing:'0.5px',marginBottom:'8px'}}>WhatsApp Number (for payment alerts)</div>
            <input value={setup.contact_phone||''} onChange={e=>setSetup({...setup,contact_phone:e.target.value})}
              placeholder="e.g. 9876543210"
              style={{width:'100%',padding:'12px 16px',border:'1px solid #e2e8f0',borderRadius:'10px',
                fontSize:'14px',boxSizing:'border-box',outline:'none'}}/>
            <div style={{fontSize:'11px',color:'#94a3b8',marginTop:'4px'}}>
              Install Android app on the phone that receives bank credit SMS
            </div>
          </div>

          {msg && (
            <div style={{background:msg.type==='success'?'#f0fdf4':'#fef2f2',
              border:'1px solid '+(msg.type==='success'?'#bbf7d0':'#fecaca'),
              borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',
              color:msg.type==='success'?'#16a34a':'#dc2626',fontSize:'13px',fontWeight:'600'}}>
              {msg.type==='success'?'✅':'❌'} {msg.text}
            </div>
          )}

          <button onClick={save} disabled={saving}
            style={{width:'100%',padding:'14px',background:primary,color:'white',border:'none',
              borderRadius:'12px',cursor:'pointer',fontSize:'15px',fontWeight:'800',
              opacity:saving?0.7:1}}>
            {saving?'Saving...':'💾 Save Payment Setup'}
          </button>

          {/* Info cards */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginTop:'20px'}}>
            {[
              {icon:'₹0',l:'Zero Fees',d:'UPI payments have zero transaction charges'},
              {icon:'⚡',l:'Instant',d:'Money credited directly to your bank account'},
              {icon:'🔒',l:'Secure',d:'End-to-end encrypted payment processing'},
              {icon:'📱',l:'Any UPI App',d:'Works with GPay, PhonePe, Paytm, CRED'},
            ].map(c=>(
              <div key={c.l} style={{background:'#f8fafc',borderRadius:'12px',padding:'14px',border:'1px solid #f1f5f9'}}>
                <div style={{fontSize:'20px',fontWeight:'900',color:primary}}>{c.icon}</div>
                <div style={{fontWeight:'700',fontSize:'13px',color:'#0f172a',marginTop:'4px'}}>{c.l}</div>
                <div style={{fontSize:'11px',color:'#64748b',marginTop:'2px'}}>{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Android App Tab */}
      {tab==='android' && (
        <div style={{background:'white',borderRadius:'16px',padding:'24px',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
          <div style={{textAlign:'center',marginBottom:'24px'}}>
            <div style={{fontSize:'48px',marginBottom:'12px'}}>📱</div>
            <h3 style={{margin:0,fontSize:'18px',fontWeight:'800',color:'#0f172a'}}>Android Observer App</h3>
            <p style={{margin:'8px 0 0',fontSize:'13px',color:'#64748b',maxWidth:'400px',marginInline:'auto'}}>
              Install this app on the Android phone that receives your bank credit SMS. 
              It automatically detects payments and confirms them in FibraDesk.
            </p>
          </div>

          <div style={{background:'#f8fafc',borderRadius:'12px',padding:'16px',marginBottom:'16px'}}>
            <div style={{fontWeight:'700',fontSize:'13px',color:'#0f172a',marginBottom:'8px'}}>
              📋 Setup Steps:
            </div>
            {[
              'Download the Observer APK on your Android phone (link below)',
              'Install the APK (Settings → Security → Allow unknown sources if asked)',
              'Open the YOUPI Observer app',
              'Enter the Gateway URL and Enrollment Token shown below',
              'Tap "Pair with Merchant Gateway"',
              'Grant Notification Access and SMS permissions when asked',
              'Go to Settings → Apps → YOUPI Observer → Battery → Unrestricted (important!)',
              'Done! Payments will auto-confirm in the dashboard'
            ].map((s,i)=>(
              <div key={i} style={{display:'flex',gap:'10px',alignItems:'flex-start',marginBottom:'8px'}}>
                <div style={{background:primary,color:'white',borderRadius:'50%',width:'20px',height:'20px',
                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'800',flexShrink:0}}>
                  {i+1}
                </div>
                <div style={{fontSize:'13px',color:'#475569'}}>{s}</div>
              </div>
            ))}
          </div>

          <a href="https://desk.pdic.support/youpi-sms-gateway.apk" target="_blank" rel="noopener noreferrer" download="youpi-sms-gateway.apk"
            style={{display:'block',textAlign:'center',background:'#16a34a',color:'white',
              padding:'14px',borderRadius:'12px',textDecoration:'none',fontWeight:'800',fontSize:'15px',
              marginBottom:'16px'}}>
            📥 Download Android APK
          </a>

          <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'10px',
            padding:'16px',fontSize:'12px',color:'#15803d',marginBottom:'12px'}}>
            <div style={{fontWeight:'800',fontSize:'13px',marginBottom:'10px'}}>📡 Pairing Details — Enter in Android App:</div>
            <div style={{background:'white',borderRadius:'8px',padding:'10px',marginBottom:'8px'}}>
              <div style={{fontSize:'11px',color:'#64748b',marginBottom:'2px'}}>Gateway URL:</div>
              <div style={{fontWeight:'700',fontSize:'13px',color:'#0f172a',wordBreak:'break-all'}}>https://pay.pdic.support</div>
            </div>
            <div style={{background:'white',borderRadius:'8px',padding:'10px'}}>
              <div style={{fontSize:'11px',color:'#64748b',marginBottom:'2px'}}>Enrollment Token:</div>
              <div style={{fontWeight:'700',fontSize:'12px',color:'#0f172a',wordBreak:'break-all',fontFamily:'monospace'}}>{androidConfig?.enrollment_token||'Contact admin for token'}</div>
            </div>
            <div style={{marginTop:'8px',fontSize:'11px',color:'#16a34a'}}>
              ⚠️ Keep the Enrollment Token private. Contact your admin if you need it.
            </div>
          </div>
          {androidConfig?.device && (
            <div style={{background: androidConfig.device.status==='CONNECTED'?'#f0fdf4':'#fef9c3',
              border:`1px solid ${androidConfig.device.status==='CONNECTED'?'#bbf7d0':'#fde68a'}`,
              borderRadius:'10px',padding:'12px',fontSize:'12px',marginBottom:'12px'}}>
              <div style={{fontWeight:'800',fontSize:'13px',marginBottom:'8px'}}>
                {androidConfig.device.status==='CONNECTED'?'✅':'📱'} Observer Device Status
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px'}}>
                <div>
                  <div style={{fontSize:'10px',color:'#64748b'}}>Device</div>
                  <div style={{fontWeight:'600'}}>{androidConfig.device.name}</div>
                </div>
                <div>
                  <div style={{fontSize:'10px',color:'#64748b'}}>Status</div>
                  <div style={{fontWeight:'700',color:androidConfig.device.status==='CONNECTED'?'#16a34a':'#d97706'}}>
                    {androidConfig.device.status}
                  </div>
                </div>
                <div>
                  <div style={{fontSize:'10px',color:'#64748b'}}>Last Seen</div>
                  <div style={{fontWeight:'600',fontSize:'11px'}}>{androidConfig.device.lastSeenAt||'-'}</div>
                </div>
                <div>
                  <div style={{fontSize:'10px',color:'#64748b'}}>Last Payment SMS</div>
                  <div style={{fontWeight:'600',fontSize:'11px'}}>{androidConfig.device.lastObservationAt||'-'}</div>
                </div>
              </div>
            </div>
          )}

          {!setup.is_active && (
            <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'10px',
              padding:'12px',fontSize:'12px',color:'#92400e',textAlign:'center'}}>
              ⚠️ Complete the Setup tab first before pairing the Android app
            </div>
          )}
        </div>
      )}

      {/* How It Works Tab */}
      {tab==='howto' && (
        <div style={{background:'white',borderRadius:'16px',padding:'24px',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
          <h3 style={{margin:'0 0 20px',fontSize:'16px',fontWeight:'800',color:'#0f172a'}}>
            📖 How Payment Works
          </h3>
          {[
            {icon:'1️⃣',title:'LCO sends payment link',desc:'From Collections tab, click a customer → Send Payment Link → WhatsApp message sent automatically'},
            {icon:'2️⃣',title:'Customer receives WhatsApp',desc:'Customer gets a secure payment link with their plan details and exact amount'},
            {icon:'3️⃣',title:'Customer pays via UPI',desc:'Customer opens link → scans QR or taps GPay/PhonePe → pays plan amount'},
            {icon:'4️⃣',title:'Money goes to LCO bank',desc:'Payment credited directly to your bank account — zero fees, instant settlement'},
            {icon:'5️⃣',title:'Auto-confirmation',desc:'Android app reads bank SMS → FibraDesk automatically marks customer as Collected'},
            {icon:'6️⃣',title:'Dashboard updates',desc:'Collections tab shows payment confirmed with UTR number for reference'},
          ].map((s,i)=>(
            <div key={i} style={{display:'flex',gap:'14px',marginBottom:'16px',
              background:'#f8fafc',borderRadius:'12px',padding:'14px'}}>
              <div style={{fontSize:'24px',flexShrink:0}}>{s.icon}</div>
              <div>
                <div style={{fontWeight:'700',fontSize:'13px',color:'#0f172a'}}>{s.title}</div>
                <div style={{fontSize:'12px',color:'#64748b',marginTop:'3px'}}>{s.desc}</div>
              </div>
            </div>
          ))}
          <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'12px',
            padding:'14px',fontSize:'12px',color:'#1e40af',textAlign:'center',fontWeight:'600'}}>
            💡 Tip: UPI payments are completely FREE — no transaction charges for you or your customer
          </div>
        </div>
      )}
    </div>
  );
}
